import MenuItem from "../../models/struct/MenuItem";
import {
  LayoutDashboard,
  Users,
  Receipt,
  Tags,
  Building2,
  Briefcase,
  Settings,
  MapPin,
} from "lucide-react";
import React from "react";

export const getMenuItems = (
  userRole: string | undefined,
  t: {
    common: {
      save: string;
      saving: string;
      loading: string;
      cancel: string;
      confirm: string;
      delete: string;
      edit: string;
      back: string;
      search: string;
      noResults: string;
      error: string;
      success: string;
      copyEmail: string;
      copied: string;
    };
    nav: {
      dashboard: string;
      members: string;
      users: string;
      admins: string;
      accountants: string;
      clients: string;
      expenses: string;
      categories: string;
      offices: string;
      companyPolicies: string;
      corporate: string;
      settings: string;
      openMenu: string;
      notacitoLogo: string;
      account: string;
      manageSubscription: string;
      logout: string;
      help: string;
      profileOverview: string;
      options: string;
      defaultUser: string;
      expandMenu: string;
      collapseMenu: string;
      plusDescription: string;
      viewOffer: string;
    };
    account: {
      pageTitle: string;
      sidebarLabel: string;
      myAccount: string;
      security: string;
      manageSubscription: string;
      myProfile: string;
      firstName: string;
      lastName: string;
      myEmail: string;
      verified: string;
      phone: string;
      phonePlaceholder: string;
      toastSuccess: string;
      toastError: string;
    };
    preferences: {
      title: string;
      language: string;
      theme: string;
      lightMode: string;
      darkMode: string;
    };
    security: {
      title: string;
      description: string;
      password: string;
      lastModified: string;
      dangerZone: string;
      dangerDesc: string;
      logout: string;
    };
    subscription: {
      title: string;
      description: string;
      freePlanDesc: string;
      activePlanDesc: string;
      upgradeCta: string;
      status: string;
      plan: string;
      plans: {
        free: string;
        essential: string;
        pro: string;
        enterprise: string;
      };
    };
    dashboard: {
      title: string;
      subtitle: string;
      loading: string;
      errorLoading: string;
      totalMembers: string;
      activeCategories: string;
      expenseReports: string;
      chartByType: string;
      chartByUsers: string;
      chartByStatus: string;
      chartOverTime: string;
      tooltipAmount: string;
      unknownRole: string;
      unknownStatus: string;
    };
    home: {
      title: string;
      subtitle: string;
      totalUsers: string;
      companies: string;
      active: string;
      blocked: string;
      left: string;
      searchPlaceholder: string;
      noUsersFound: string;
      noSearchMatch: string;
      failedToLoad: string;
      userStatusUpdated: string;
      blockUser: string;
      activateUser: string;
      usersCount: string;
      usersCountPlural: string;
      table: {
        name: string;
        email: string;
        role: string;
        phone: string;
        status: string;
        access: string;
      };
      page: string;
    };
    searchPlaceholderCompany: string;
    pagination: { result: string; results: string };
    clients: {
      title: string;
      subtitle: string;
      addBtn: string;
      total: string;
      block: string;
      activate: string;
      noMatch: string;
      notFound: string;
      statusUpdated: string;
      failedToLoad: string;
      count: string;
      countPlural: string;
    };
    accountants: {
      title: string;
      subtitle: string;
      addBtn: string;
      total: string;
      block: string;
      activate: string;
      noMatch: string;
      notFound: string;
      statusUpdated: string;
      failedToLoad: string;
      count: string;
      countPlural: string;
    };
    admins: {
      title: string;
      subtitle: string;
      addBtn: string;
      total: string;
      block: string;
      activate: string;
      noMatch: string;
      notFound: string;
      statusUpdated: string;
      failedToLoad: string;
      count: string;
      countPlural: string;
    };
    expenseReports: {
      title: string;
      subtitle: string;
      noCompany: string;
      unknownUser: string;
      statusDraft: string;
      statusPending: string;
      statusApproved: string;
      statusReimbursed: string;
      statusRejected: string;
      pendingAlertSingle: string;
      pendingAlertPlural: string;
      pendingLabel: string;
      approvedAlertSingle: string;
      approvedAlertPlural: string;
      approvedLabel: string;
      noAlerts: string;
      monthTotal: string;
      noExpenses: string;
      reportCountSingle: string;
      reportCountPlural: string;
      userCountSingle: string;
      userCountPlural: string;
      noExpensesThisMonth: string;
      showingExpensesFor: string;
      only: string;
      noUsersMatch: string;
      showingSearchMatch: string;
      noReportsFound: string;
      tableSupplier: string;
      tableCategory: string;
      tableDate: string;
      tableAmount: string;
      tableVat: string;
      tableStatus: string;
      statTotalReports: string;
      statCompanies: string;
      statMonthTotal: string;
      statPending: string;
      statApproved: string;
      statReimbursed: string;
      searchPlaceholder: string;
      failedToLoad: string;
    };
    offices: {
      title: string;
      subtitle: string;
      loading: string;
      errorLoading: string;
      noOffices: string;
      addBtn: string;
    };
    officeCell: {
      noName: string;
      mainPlatform: string;
      standardCorporate: string;
    };
    officeDetails: { loading: string; errorPrefix: string; notFound: string };
    companyPolicies: {
      title: string;
      loading: string;
      deleteSuccess: string;
      deleteConfirm: string;
      addBtn: string;
      deleting: string;
      deleteBtn: string;
      editBtn: string;
      status: string;
      dailyLimit: string;
      vatRule: string;
      receiptRequired: string;
      manualVat: string;
      active: string;
      allowed: string;
      forbidden: string;
      noLimit: string;
      yes: string;
      no: string;
      selectCategory: string;
    };
    corporate: {
      loading: string;
      errorLoading: string;
      title: string;
      subtitle: string;
      statActive: string;
      statTopStreak: string;
      statTotalFlames: string;
      liveLeaderboard: string;
      liveBadge: string;
      noActiveParticipants: string;
      historicalPodium: string;
      btnSnapshot: string;
      btnSaving: string;
      snapshotSuccess: string;
      snapshotError: string;
      snapshotConfirm: string;
      noDataRecorded: string;
      howItWorksTitle: string;
      howItWorksL1: string;
      howItWorksL2: string;
      howItWorksL3: string;
      howItWorksL4: string;
      chartTitle: string;
    };
    auth: {
      login: string;
      loginTitle: string;
      email: string;
      password: string;
      forgotPassword: string;
      noAccount: string;
      register: string;
      loginError: string;
    };
    errors: {
      required: string;
      invalidEmail: string;
      networkError: string;
      unauthorized: string;
      notFound: string;
    };
  },
): MenuItem[] => {
  const baseMenu: MenuItem[] = [
    {
      path: "/dashboard",
      label: t.nav.dashboard,
      icon: React.createElement(LayoutDashboard, { size: 20 }),
    },
    {
      label: t.nav.members,
      icon: React.createElement(Users, { size: 20 }),
      children: [
        { path: "/users", label: t.nav.users },
        { path: "/admins", label: t.nav.admins },
        { path: "/accountants", label: t.nav.accountants },
        { path: "/clients", label: t.nav.clients },
      ],
    },
    {
      path: "/expense-reports",
      label: t.nav.expenses,
      icon: React.createElement(Receipt, { size: 20 }),
    },
    {
      path: "/categories",
      label: t.nav.categories,
      icon: React.createElement(Tags, { size: 20 }),
    },
    {
      path: "/company-policies",
      label: t.nav.companyPolicies,
      icon: React.createElement(Briefcase, { size: 20 }),
    },
    {
      path: "/corporate",
      label: t.nav.corporate,
      icon: React.createElement(Building2, { size: 20 }),
    },
    {
      path: "/settings",
      label: t.nav.settings,
      icon: React.createElement(Settings, { size: 20 }),
    },
  ];

  if (userRole === "super_admin") {
    const categoriesIndex = baseMenu.findIndex(
      (item) => item.path === "/categories",
    );

    if (categoriesIndex !== -1) {
      baseMenu.splice(categoriesIndex, 0, {
        path: "/offices",
        label: t.nav.offices,
        icon: React.createElement(MapPin, { size: 20 }),
      });
    }
  }

  if (userRole === "accountant") {
    return baseMenu.filter(
      (item) => item.path !== "/company-policies" && item.path !== "/corporate",
    );
  }

  return baseMenu;
};
